<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="visible" class="password-modal__backdrop" @click.self.prevent>
        <section class="password-modal">
          <header class="password-modal__header">
            <h3 class="password-modal__title">{{ $t('auth.forceChangePassword.title') }}</h3>
            <button type="button" class="password-modal__close" @click="handleDismiss">
              <i class="zmdi zmdi-close" />
            </button>
          </header>

          <p class="password-modal__desc">{{ $t('auth.forceChangePassword.subtitle') }}</p>

          <form @submit.prevent="handleSubmit" class="password-modal__content">
            <div class="password-modal__field">
              <div class="password-modal__input-wrapper">
                <input
                  ref="passwordInput"
                  v-model="form.password"
                  :type="showPass ? 'text' : 'password'"
                  :placeholder="$t('profile.newPassword')"
                  class="password-modal__input"
                  autocomplete="new-password"
                />
                <button type="button"
class="password-modal__toggle"
tabindex="-1"
@click="showPass = !showPass">
                  <i :class="showPass ? 'zmdi zmdi-eye-off' : 'zmdi zmdi-eye'" />
                </button>
              </div>
            </div>

            <div class="password-modal__field">
              <div class="password-modal__input-wrapper">
                <input
                  v-model="form.confirm"
                  :type="showPass ? 'text' : 'password'"
                  :placeholder="$t('profile.repeatNewPassword')"
                  class="password-modal__input"
                  :class="{ 'password-modal__input--error': form.confirm && !validation.passwordsMatch }"
                  autocomplete="new-password"
                />
                <button type="button"
class="password-modal__toggle"
tabindex="-1"
@click="showPass = !showPass">
                  <i :class="showPass ? 'zmdi zmdi-eye-off' : 'zmdi zmdi-eye'" />
                </button>
              </div>
            </div>

            <ul v-if="form.password" class="password-modal__rules">
              <li :class="{ 'password-modal__rule--valid': validation.minLength }">
                <i :class="validation.minLength ? 'zmdi zmdi-check' : 'zmdi zmdi-close'" />
                {{ $t('auth.forceChangePassword.errorMinLength') }}
              </li>
              <li :class="{ 'password-modal__rule--valid': validation.hasUppercase }">
                <i :class="validation.hasUppercase ? 'zmdi zmdi-check' : 'zmdi zmdi-close'" />
                {{ $t('auth.forceChangePassword.errorNeedUppercase') }}
              </li>
              <li :class="{ 'password-modal__rule--valid': validation.hasLowercase }">
                <i :class="validation.hasLowercase ? 'zmdi zmdi-check' : 'zmdi zmdi-close'" />
                {{ $t('auth.forceChangePassword.errorNeedLowercase') }}
              </li>
              <li :class="{ 'password-modal__rule--valid': validation.hasDigit }">
                <i :class="validation.hasDigit ? 'zmdi zmdi-check' : 'zmdi zmdi-close'" />
                {{ $t('auth.forceChangePassword.errorNeedDigit') }}
              </li>
              <li :class="{ 'password-modal__rule--valid': validation.hasSymbol }">
                <i :class="validation.hasSymbol ? 'zmdi zmdi-check' : 'zmdi zmdi-close'" />
                {{ $t('auth.forceChangePassword.errorNeedSpecial') }}
              </li>
              <li v-if="form.confirm" :class="{ 'password-modal__rule--valid': validation.passwordsMatch }">
                <i :class="validation.passwordsMatch ? 'zmdi zmdi-check' : 'zmdi zmdi-close'" />
                {{ $t('auth.forceChangePassword.errorMismatch') }}
              </li>
            </ul>

            <p v-if="apiError" class="password-modal__error">{{ apiError }}</p>

            <button type="submit" class="password-modal__submit" :disabled="!isValid || submitting">
              <i v-if="submitting" class="zmdi zmdi-spinner zmdi-hc-spin mr-2" />
              {{ submitting ? $t('profile.changingPassword') : $t('auth.forceChangePassword.submit') }}
            </button>

            <button type="button" class="password-modal__logout" @click="handleLogout">
              {{ $t('auth.forceChangePassword.logout') }}
            </button>
          </form>
        </section>
      </div>
    </transition>

    <!-- SecurityCodeModal: sibling, NOT nested. Shows on top with its own backdrop.
         Pattern identical to WithdrawModal / SendFundsModal. -->
    <SecurityCodeModal
      :visible="isSecurityVisible"
      action-name="force_change_pass"
      @close="isSecurityVisible = false"
      @confirm="handleSecurityConfirm"
    />
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import { ProfileApi } from '@/modules/profile/api/profileApi'
import SecurityCodeModal from '@/modules/wallet/components/SecurityCodeModal.vue'
import iziToast from 'izitoast'
import { useI18n } from 'vue-i18n'

const ERROR_PASSWORD_SAME_AS_OLD = 1400
const ERROR_PASSWORD_CHANGE_NOT_REQUIRED = 1401
const ERROR_PASSWORD_FORCE_CHANGE_TOO_MANY_ATTEMPTS = 1402

const { t } = useI18n()
const profileStore = useProfileStore()

const dismissed = ref(false)

const visible = computed(() =>
  !dismissed.value && profileStore.profile?.security?.password_change_required === true
)

const form = ref({ password: '', confirm: '' })
const showPass = ref(false)
const submitting = ref(false)
const apiError = ref('')
const passwordInput = ref<HTMLInputElement>()
const isSecurityVisible = ref(false)

const validation = computed(() => {
  const p = form.value.password
  return {
    minLength: p.length >= 8,
    hasUppercase: /[A-Z]/.test(p),
    hasLowercase: /[a-z]/.test(p),
    hasDigit: /[0-9]/.test(p),
    hasSymbol: /[^A-Za-z0-9]/.test(p),
    passwordsMatch: p === form.value.confirm && form.value.confirm.length > 0,
  }
})

const isValid = computed(() => Object.values(validation.value).every(Boolean))

watch(visible, (v) => {
  if (v) {
    form.value = { password: '', confirm: '' }
    apiError.value = ''
    isSecurityVisible.value = false
    nextTick(() => passwordInput.value?.focus())
  }
})

// Step 1: user clicks "Сменить пароль" → open SecurityCodeModal
function handleSubmit() {
  if (!isValid.value) return
  apiError.value = ''
  isSecurityVisible.value = true
}

// Step 2: SecurityCodeModal emits entered code (NOT verified yet).
// Pass code as confirmActionCode → backend verifies via SpaceGuard::checkConfirmAction.
async function handleSecurityConfirm(code: string) {
  isSecurityVisible.value = false
  submitting.value = true
  apiError.value = ''

  try {
    await ProfileApi.forceChangePassword({
      passwordnew: form.value.password,
      passwordretry: form.value.confirm,
      confirmActionCode: code,
    })

    form.value = { password: '', confirm: '' }

    iziToast.success({
      title: t('auth.forceChangePassword.successTitle'),
      message: t('auth.forceChangePassword.successMessage'),
      position: 'topRight',
    })

    // Force-refetch profile → password_change_required becomes false → modal auto-hides.
    // force=true is required — without it, profileStore returns cached data early.
    // Fallback: if fetchProfile fails, force-close by clearing the flag in store directly.
    try {
      await profileStore.fetchProfile(true)
    } catch {
      if (profileStore.profile?.security) {
        profileStore.profile.security.password_change_required = false
      }
    }
  } catch (e: any) {
    handleApiError(e)
  } finally {
    submitting.value = false
  }
}

function handleApiError(e: any) {
  const code = e?.error_code || e?.code || e?.response?.data?.error_code
  switch (code) {
    case ERROR_PASSWORD_SAME_AS_OLD:
      apiError.value = t('auth.forceChangePassword.errorSameAsOld')
      break
    case ERROR_PASSWORD_CHANGE_NOT_REQUIRED:
      profileStore.fetchProfile()
      break
    case ERROR_PASSWORD_FORCE_CHANGE_TOO_MANY_ATTEMPTS:
      apiError.value = t('auth.forceChangePassword.errorTooManyAttempts')
      break
    default:
      apiError.value = e?.message || t('auth.forceChangePassword.errorGeneric')
  }
}

function handleDismiss() {
  // Позволяем закрыть попап крестиком — юзер увидит его снова при следующей загрузке base-info.
  // Для этого не меняем store, а скрываем через локальный флаг.
  dismissed.value = true
}

function handleLogout() {
  window.location.href = '/logout'
}
</script>

<style scoped lang="scss">
.password-modal__backdrop {
  position: fixed;
  inset: 0;
  background: var(--ds-scrim);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 1300;
}

.password-modal {
  width: min(420px, 100%);
  max-height: 85vh;
  background: var(--ds-surface);
  border-radius: var(--ds-radius-modal);
  box-shadow: 0 28px 48px var(--ds-scrim-faint);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.password-modal__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 24px 0;
}

.password-modal__title {
  flex: 1;
  margin: 0;
  font-size: var(--ds-text-size-h-sm);
  font-weight: var(--ds-weight-semibold);
  color: var(--hm-text-primary);
}

.password-modal__close {
  width: 40px;
  height: 40px;
  border: none;
  background: none;
  color: var(--hm-text-muted);
  font-size: var(--ds-text-size-h-sm);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--hm-text-primary);
  }
}

.password-modal__desc {
  padding: 8px 24px 0;
  margin: 0;
  font-size: var(--ds-text-size-body);
  color: var(--hm-text-muted);
  line-height: 1.4;
}

.password-modal__content {
  padding: 20px 24px 24px;
  overflow-y: auto;
}

.password-modal__field { margin-bottom: 12px; }

.password-modal__input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-modal__input {
  width: 100%;
  padding: 14px 48px 14px 16px;
  border-radius: var(--p-radius-14);
  border: 1px solid var(--hm-border);
  background: var(--hm-surface-alt);
  font-size: var(--ds-text-size-body-lg);
  color: var(--hm-text-primary);
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--ds-accent-soft-32);
    box-shadow: 0 0 0 4px var(--ds-accent-soft-12);
  }
  &::placeholder { color: var(--hm-text-muted); }
  &--error {
    border-color: var(--ds-danger);
    &:focus { box-shadow: 0 0 0 4px var(--ds-danger-soft); }
  }
}

.password-modal__toggle {
  position: absolute;
  right: 12px;
  width: 32px; height: 32px;
  border: none; background: none;
  color: var(--hm-text-muted);
  font-size: var(--p-text-18); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  &:hover { color: var(--hm-text-secondary); }
}

.password-modal__rules {
  list-style: none;
  margin: 0 0 16px;
  padding: 12px 16px;
  background: var(--hm-surface-alt);
  border-radius: var(--ds-radius-card);
  font-size: var(--p-text-13);

  li {
    display: flex; align-items: center; gap: 8px; padding: 4px 0;
    color: var(--hm-text-muted);
    i { font-size: var(--ds-text-size-body); color: var(--ds-danger); }
  }
}

.password-modal__rule--valid {
  color: var(--hm-text-secondary) !important;
  i { color: var(--ds-success) !important; }
}

.password-modal__error {
  color: var(--ds-danger); font-size: var(--p-text-13); margin: -4px 0 12px; padding: 0;
}

.password-modal__submit {
  width: 100%;
  padding: 14px 20px;
  border-radius: var(--p-radius-50); border: none;
  background: linear-gradient(180deg, var(--ds-accent-light), var(--ds-accent));
  color: var(--ds-text-on-accent); font-size: var(--p-text-15); font-weight: var(--ds-weight-semibold);
  cursor: pointer; transition: all 0.2s ease;
  margin-bottom: 12px;

  &:hover:not(:disabled) {
    box-shadow: 0 8px 20px var(--ds-accent-soft-28);
    transform: translateY(-1px);
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.password-modal__logout {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--hm-border);
  border-radius: var(--p-radius-50); background: transparent;
  color: var(--hm-text-muted); font-size: var(--ds-text-size-body);
  cursor: pointer; transition: all 0.2s ease;
  &:hover { color: var(--ds-danger); border-color: var(--ds-danger); }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.24s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
